import Link from '@/components/Link';
import Styled from '@/components/vizy/VizyTextPage.styled';
import gtm from '@/lib/gtm';
import { normalizeCmsQuoteString } from '@/lib/parse-html';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { extractYouTubeId } from '@/lib/youtube';
import { decode } from 'html-entities';
import React, { FC } from 'react';
import PullQuote from '../_blog-updates/PullQuote';

const parseVizyNode = (node: VizyNode, index: number, nodeDetail?: any) => {
  if (node.type === 'text') {
    node.text = decode(node.text);

    return parseVizyMarks(node.marks, node.text);
  }

  let content: React.ReactNode[] = [];

  if (node.content) {
    content = node.content?.map((node, index) => parseVizyNode(node, index));
  }

  if (node.type === 'paragraph') {
    return (
      <Styled.Paragraph textAlign={node.attrs?.textAlign}>{!!content.length ? content : '\u00A0'}</Styled.Paragraph>
    );
  } else if (node.type === 'codeBlock') {
    return <Styled.CodeBlock>{content}</Styled.CodeBlock>;
  } else if (node.type === 'hardBreak') {
    return <br />;
  } else if (node.type === 'heading') {
    switch (node.attrs?.level) {
      case 1:
        return <Styled.Heading1 textAlign={node.attrs?.textAlign}>{content}</Styled.Heading1>;
      case 2:
        return <Styled.Heading2 textAlign={node.attrs?.textAlign}>{content}</Styled.Heading2>;
      case 3:
        return <Styled.Heading3 textAlign={node.attrs?.textAlign}>{content}</Styled.Heading3>;
      case 4:
        return <Styled.Heading4 textAlign={node.attrs?.textAlign}>{content}</Styled.Heading4>;
      case 5:
        return <Styled.Heading5 textAlign={node.attrs?.textAlign}>{content}</Styled.Heading5>;
      default:
        return <Styled.Heading6 textAlign={node.attrs?.textAlign}>{content}</Styled.Heading6>;
    }
  } else if (node.type === 'horizontalRule') {
    return <Styled.HorizontalRule />;
  } else if (node.type === 'orderedList') {
    return <Styled.OrderedList start={node.attrs?.start}>{content}</Styled.OrderedList>;
  } else if (node.type === 'bulletList') {
    return <Styled.UnorderedList>{content}</Styled.UnorderedList>;
  } else if (node.type === 'listItem') {
    return (
      <Styled.ListItem>
        <Styled.Paragraph>{content}</Styled.Paragraph>
      </Styled.ListItem>
    );
  } else if (node.type === 'vizyBlock') {
    switch (nodeDetail?.blockTypeHandle) {
      case 'blockquoteLinked':
        return nodeDetail?.associatedQuotes?.map((quote, i) => (
          <PullQuote
            key={quote.quote ?? i}
            quote={normalizeCmsQuoteString(quote.quote ?? '')}
            authorName={quote.authorName ?? ''}
            authorTitle={quote.authorTitle ?? ''}
          />
        ));
      case 'blockquoteStandalone':
        const pqImage = nodeDetail?.pullQuoteImage?.[0];

        return (
          <PullQuote
            quote={nodeDetail?.quote ?? ''}
            authorName={nodeDetail?.authorName ?? ''}
            authorTitle={nodeDetail?.authorTitle ?? ''}
            image={pqImage?.url}
          />
        );
      case 'image':
        const image = nodeDetail?.image?.[0];

        if (!image) return null;

        return (
          <Styled.Figure>
            <Styled.Image
              src={image.url}
              width={image.width}
              height={image.height}
              alt={image.altText}
              rounded={nodeDetail?.hasRoundedCorners}
            />

            {nodeDetail?.imageCaption && <figcaption>{nodeDetail?.imageCaption}</figcaption>}
          </Styled.Figure>
        );
      case 'youtubeVideo':
        const youtubeVideoUrl = nodeDetail?.youtubeVideoUrl;

        if (!youtubeVideoUrl) return null;

        const youtubeVideoId = extractYouTubeId(youtubeVideoUrl);

        return (
          <Styled.YouTubeVideo
            videoId={youtubeVideoId!}
            coverSrc={nodeDetail?.coverImage?.[0]?.url!}
            onInitialPlay={() => {
              gtm.track('play_video', {
                video_title: maybeNotSet(nodeDetail?.videoLabel),
                video_url: maybeNotSet(youtubeVideoUrl),
                video_youtube_id: maybeNotSet(youtubeVideoId),
                location: 'Blog Article',
              });
            }}
          />
        );
      case 'videoAsset':
        const videoUrl = nodeDetail?.videoAsset?.[0]?.url;

        if (!videoUrl) return null;

        return (
          <Styled.Video
            videoUrl={videoUrl}
            onInitialPlay={() => {
              gtm.track('play_video', {
                video_title: maybeNotSet(nodeDetail?.videoLabel),
                video_url: maybeNotSet(videoUrl),
              });
            }}
          />
        );
      default:
        return null;
    }
  }

  return null;
};

const parseVizyMarks = (marks: VizyMark[] | undefined, text: string) => {
  if (!marks) return text;

  return marks.reduce((content, mark) => {
    if (mark.type === 'bold') {
      return <strong>{content}</strong>;
    } else if (mark.type === 'italic') {
      return <em>{content}</em>;
    } else if (mark.type === 'underline') {
      return <u>{content}</u>;
    } else if (mark.type === 'highlight') {
      return <Styled.Highlight>{content}</Styled.Highlight>;
    } else if (mark.type === 'strike') {
      return <Styled.Strikethrough>{content}</Styled.Strikethrough>;
    } else if (mark.type === 'code') {
      return <Styled.InlineCode>{content}</Styled.InlineCode>;
    } else if (mark.type === 'subscript') {
      return <sub>{content}</sub>;
    } else if (mark.type === 'superscript') {
      return <sup>{content}</sup>;
    } else if (mark.type === 'link') {
      return (
        <Link href={mark.attrs?.href ?? '#'}>
          <Styled.TextLink
            target={mark.attrs?.target}
            onClick={() => {
              gtm.track('click', {
                element_type: 'link',
                element_label: maybeNotSet(text.toString()),
                location: 'Content',
              });
            }}
          >
            {content}
          </Styled.TextLink>
        </Link>
      );
    }

    return content;
  }, text);
};

interface VizyTextPageProps {
  rawNodes: string;
  nodes: any;
}

const VizyTextPage: FC<VizyTextPageProps> = ({ rawNodes, nodes }) => {
  const parsedNodes: VizyNode[] = JSON.parse(JSON.stringify(rawNodes));

  return <>{parsedNodes.map((node, index) => parseVizyNode(node, index, nodes[index]))}</>;
};

export default VizyTextPage;
